import React, { useState, useEffect, useRef } from "react";
import {
  Phone,
  Shield,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Loader2,
  KeyRound,
} from "lucide-react";
import { GET_OTP_FOR_LOGIN, VERIFY_OTP } from "../../constants";
import { useNavigate } from "react-router-dom";
import logo from "../../icons/ABIS_logo.png";

const CustomerLogin = () => {
  const [step, setStep] = useState("phone"); // 'phone' or 'otp'
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [otpId, setOtpId] = useState("");
  const navigate = useNavigate();
  const otpInputs = useRef([]);

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((timer) => timer - 1);
      }, 1000);
    } else if (timer === 0 && step === "otp") {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer, step]);

  const validatePhone = (phoneNumber) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleSendOTP = async () => {
    if (!validatePhone(phone)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetch(GET_OTP_FOR_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: phone }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setOtpId(data.data.otp_id);
        setStep("otp");
        setTimer(60);
        setCanResend(false);
      } else {
        setError(data.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError("");

    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }

    if (newOtp.every((digit) => digit !== "")) {
      handleVerifyOTP(newOtp.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (otpValue = otp.join("")) => {
    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetch(VERIFY_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile_number: phone,
          otp: otpValue,
          otp_id: otpId,
        }),
      });
      const data = await response.json();

      const kycData = {
        lead_id: data.data?.session_info?.lead_id,
        mobile_number: data.data?.customer_info?.mobile,
      };
      if (response.ok && data.success) {
        alert("Login successful! Redirecting...");
        localStorage.setItem("eKYCData", JSON.stringify(kycData));
        navigate("/customer/dashboard");
      } else {
        setError(data.message || "Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        otpInputs.current[0]?.focus();
      }
    } catch (error) {
      setError("Network error. Please check your connection and try again.");
      setOtp(["", "", "", "", "", ""]);
      otpInputs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setTimer(60);
    setCanResend(false);
    setError("");
    setOtp(["", "", "", "", "", ""]);
    try {
      const response = await fetch(GET_OTP_FOR_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: phone }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setOtpId(data.data.otp_id);
        alert("A new OTP has been sent successfully!");
      } else {
        setError(data.message || "Failed to resend OTP.");
        setTimer(0);
        setCanResend(true);
      }
    } catch (error) {
      setError("Network error. Please try again.");
      setTimer(0);
      setCanResend(true);
    }
  };

  const handleBack = () => {
    setStep("phone");
    setOtp(["", "", "", "", "", ""]);
    setError("");
    setTimer(0);
    setOtpId("");
  };

  const renderPhoneStep = () => (
    <div className="space-y-6 animate-fade-in max-w-md mx-auto px-4 sm:px-6 lg:px-8">
      {/* Company Logo */}
      <div className="flex justify-center">
        <img
          src={logo} // replace with your logo path
          alt="abis exports Logo"
          className="h-12 w-auto sm:h-16 object-contain"
        />
      </div>

      <div className="text-center">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
          Enter Mobile Number
        </h2>
        <p className="text-gray-500 mt-1 text-sm sm:text-base">
          We'll send a secure OTP to your phone.
        </p>
      </div>

      <div className="relative">
        <Phone className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="tel"
          value={phone}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 10);
            setPhone(value);
            setError("");
          }}
          placeholder="10-digit mobile number"
          className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
          maxLength="10"
        />
      </div>
      <button
        onClick={handleSendOTP}
        disabled={loading || phone.length !== 10}
        className="w-full bg-blue-600 text-white py-3 sm:py-3.5 rounded-xl font-semibold text-sm sm:text-base hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transform hover:-translate-y-0.5"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <span>Send OTP</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </>
        )}
      </button>
    </div>
  );
  const renderOtpStep = () => (
    <div className="space-y-6 animate-fade-in max-w-md mx-auto px-4 sm:px-6 lg:px-8">
      {/* Company Logo */}
      <div className="flex justify-center">
        <img
          src={logo} // replace with your company logo path
          alt="abis exports logo"
          className="h-12 w-auto sm:h-16 object-contain"
        />
      </div>

      <div className="text-center">
        <button
          onClick={handleBack}
          className="inline-flex items-center text-xs sm:text-sm text-gray-600 hover:text-blue-600 mb-3 transition-colors"
        >
          <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
          Change Number
        </button>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          Verify OTP
        </h2>
        <p className="text-gray-500 mt-1 text-sm sm:text-base">
          Enter the code sent to{" "}
          <span className="font-semibold text-gray-700">+91 {phone}</span>
        </p>
      </div>

      {/* OTP Input Boxes */}
      <div className="flex justify-center space-x-2 sm:space-x-3">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (otpInputs.current[index] = el)}
            id={`otp-${index}`}
            type="tel"
            value={digit}
            onChange={(e) => handleOTPChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-9 sm:w-12 h-10 sm:h-12 text-center text-lg sm:text-xl font-bold bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
            maxLength="1"
          />
        ))}
      </div>

      {/* Resend OTP */}
      <div className="text-center text-xs sm:text-sm">
        {timer > 0 ? (
          <p className="text-gray-500">
            Resend OTP in{" "}
            <span className="font-semibold text-blue-600">{timer}s</span>
          </p>
        ) : (
          <button
            onClick={handleResendOTP}
            disabled={!canResend}
            className="text-blue-600 hover:underline font-semibold transition-all"
          >
            Resend OTP
          </button>
        )}
      </div>

      {/* Verify Button */}
      <button
        onClick={() => handleVerifyOTP()}
        disabled={loading || otp.some((digit) => !digit)}
        className="w-full bg-green-600 text-white py-3 sm:py-3.5 rounded-xl font-semibold text-sm sm:text-base hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/30 transform hover:-translate-y-0.5"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Verify & Login</span>
          </>
        )}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 items-center gap-12 max-w-5xl mx-auto">
          {/* Left Side: Branding */}
          <div className="hidden lg:block p-8">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-3 rounded-2xl">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-800">
                Customer Portal
              </h1>
            </div>
            <p className="mt-4 text-lg text-gray-600">
              Securely access your account, manage your information, and stay
              updated with the latest information.
            </p>
            <ul className="mt-6 space-y-4 text-gray-600">
              <li className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>View and manage your profile.</span>
              </li>
              <li className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Track your interactions and history.</span>
              </li>
              <li className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Get access to exclusive resources.</span>
              </li>
            </ul>
          </div>

          {/* Right Side: Login Form */}
          <div className="w-full max-w-md mx-auto lg:mx-0">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              {step === "phone" ? renderPhoneStep() : renderOtpStep()}
              {error && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg mt-4 text-sm animate-fade-in">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
            <p className="text-center text-xs text-gray-500 mt-6">
              <Shield size={14} className="inline mr-1" />
              Your information is protected with end-to-end encryption.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
